/* eslint-disable no-redeclare */
import { Position } from '@com/type'
import { TrackFrameUnit, TrackGap, TrackItem, TrackSegment } from '../types'
import { TimelineGap, TimelineOptions } from '@com/components/timeline/type'
import { SegmentPositionDesc } from '../components/track'
import { resolveGapFrames } from '@com/components/timeline/shared'

export function getBeforeGaps(
  f: TrackFrameUnit,
  gaps: TimelineOptions['gaps'],
): TrackFrameUnit {
  const beforeGapFrames = gaps
    ?.filter((g) => g.in < f)
    .reduce((frames, g) => (frames += resolveGapFrames(g)), 0)

  return beforeGapFrames ?? 0
}

export function getSegmentScopedGaps<
  T extends Pick<TrackSegment, 'start' | 'frames' | 'is'>,
>(segment: T, gaps: TimelineOptions['gaps']): TimelineGap[] {
  if (segment.is === 'gap') {
    return []
  }

  return (
    gaps?.filter(
      (g) => g.in >= segment.start && g.in < segment.start + segment.frames,
    ) ?? []
  )
}

export function segment2Gap<T extends TrackSegment>(segment: T): TimelineGap
export function segment2Gap<T extends TrackSegment>(segment: T[]): TimelineGap[]
export function segment2Gap<T extends TrackSegment>(
  segmentOrArray: T | T[],
): TimelineGap | TimelineGap[] {
  const _map = (s: T) => {
    return {
      in: s.start,
      frames: s.frames,
    }
  }

  if (Array.isArray(segmentOrArray)) {
    return segmentOrArray.map(_map)
  }

  return _map(segmentOrArray)
}

export function getGapSegments(data: TrackItem[]): TrackSegment[] {
  return data.reduce<TrackSegment[]>(
    (segments, t) => [...segments, ...t.segments.filter((s) => s.is === 'gap')],
    [],
  )
}

export function getSegmentHeight(
  data: TrackItem[],
  segment: TrackSegment,
): number {
  return data.find((t) => t.id === segment.trackId)?.height ?? 0
}

export function getSegmentPosition(
  data: TrackItem[],
  segment: TrackSegment,
  options: {
    gap: TrackGap
    gaps: TimelineOptions['gaps']
    frameWidth: TrackFrameUnit
    pos?: SegmentPositionDesc
  },
): Position {
  const { gap, frameWidth, pos, gaps = [] } = options

  if (pos && pos.movable) {
    return pos.position
  }

  const beforeGaps = gaps
    .sort((a, b) => a.in - b.in)
    .filter((g) => g.in < segment.start)
  const gapFrames = beforeGaps.reduce((frames, g) => {
    return (frames += resolveGapFrames(g))
  }, 0)

  const x = (gapFrames + segment.start) * frameWidth

  const targetTrackIndex = data.findIndex((t) => t.id === segment.trackId)

  // 计算每个轨道的高度（每个高度都会跟轨道的类型相关）
  const h = data.slice(0, targetTrackIndex + 1).reduce((h, t) => {
    return (h += t.height)
  }, 0)

  const allGap = gap * targetTrackIndex
  const bottom = allGap + h
  const height = getSegmentHeight(data, segment)
  return {
    x,
    y: bottom - height,
  }
}

/**
 * 获取gap segment的有效帧数
 * @param segment 指定元件
 * @returns 有效帧数
 */
export function resolveSegmentFramesIfGap(
  segment: TrackSegment,
): TrackSegment['frames'] {
  return segment.is === 'gap' ? 1 : segment.frames
}
